// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";
import { Button } from "react-bootstrap";

import { IAction, ActionType, raiseAction } from "actions";
import { isSet } from "shared/utils/utils";
import { getIsSiteOffline } from "guiutils";
import { IState } from "states/states";
import { Site } from "states/sitestates";
import { AppState } from "states/schedulerstates";
import SiteModel from "model/site";

import InfoScreen from "view/comp/InfoScreen";
import InfoRow from "view/comp/InfoRow";
import MyFormattedTime from "view/comp/FormattedTime";
import MyFormattedDate from "view/comp/FormattedDate";

import IconLocation from "icons/iconLocation";
import IconGrid from "icons/iconGrid";
import IconRocket from "icons/iconRocket";
import IconCode from "icons/iconCode";
import IconClock from "icons/iconClock";
import IconCalendar from "icons/iconCalendar";
import IconAlarm from "icons/navAlarm";
import IconFavorite from "icons/iconFavorite";
import IconEmptyFavorite from "icons/iconEmptyFavorite";

import "./SiteDetails.scss";
import SiteInfoTags, { getConnectedSiteAlarmCounts } from "./SiteInfoTags";

interface ISiteDetailsExtProps {
  siteId: number | undefined;
  onBack: () => void;
  // undefined if the map isn't shown; the button is hidden whenever this is
  onShowInMap?: () => void;
  isPopupOpenInMap: boolean;
}

interface ISiteDetailsProps {
  appState: AppState;
  siteList: Site[] | undefined; // undefined if not loaded yet

  selectedSite: number | undefined;
  favoriteSites: number[] | undefined;

  lastTrendData: number | undefined;
  // last live data from the connected site, unset if never received
  lastLiveData: number | undefined;
  liveViaProxy: boolean;
  parameterTimestamp: number | undefined;
  isOffline: boolean;
  connectedUnackAlarms: number | undefined;
  connectedUnclearedAlarms: number | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: ISiteDetailsProps = {
    appState: state.scheduler.appState,
    siteList: state.site.siteList ?? [],
    selectedSite: state.cached.selectedSite,
    favoriteSites: state.cached.favoriteSites,
    lastTrendData: state.scheduler.trendLoading.lastData,
    lastLiveData: state.scheduler.allData.lastData,
    liveViaProxy: state.scheduler.liveViaProxy,
    parameterTimestamp: state.para.timestamp,
    isOffline: getIsSiteOffline(state),
    ...getConnectedSiteAlarmCounts(state),
  };
  return props;
};

interface ISiteDetailsDispatch {
  onSelectSite: (id: number) => void;
  onDisconnectClick: () => void;
  onToggleFavorite: (siteId: number, doSet: boolean) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISiteDetailsDispatch = {
    onSelectSite: (id: number) => {
      dispatch(raiseAction(ActionType.ACT_SWITCH_SITE, id));
    },
    onDisconnectClick: () => {
      dispatch(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    },
    onToggleFavorite: (siteId: number, doSet: boolean) => {
      dispatch(raiseAction(ActionType.ACT_SET_FAVORITE, { doSet, siteId }));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_address: {
    id: "Address",
  },
  msg_network: {
    id: "IP Settings",
  },
  msg_back: {
    id: "Back",
  },
  msg_show_all_sites: {
    id: "Show All Sites",
  },
  msg_show_in_map: {
    id: "Show in map",
  },
  msg_serialnumber: {
    id: "Serial Number",
  },
  msg_device: {
    id: "Device",
  },
  msg_software: {
    id: "Software",
  },
  msg_trend_live_data: {
    id: "Trend / Live Data",
  },
  msg_parameters: {
    id: "Parameters",
  },
  msg_direct: {
    id: "Direct",
  },
  msg_cloud: {
    id: "Cloud",
  },
  msg_connect: {
    id: "Connect",
  },
  msg_disconnect: {
    id: "Disconnect",
  },
  msg_no_site_selected: {
    id: "No site selected",
  },
  msg_offline: {
    id: "Offline",
  },
  msg_set_favorite: {
    id: "Mark as favorite",
  },
  msg_remove_favorite: {
    id: "Remove from favorites",
  },
});

class SiteDetailsComponent extends React.Component<
  ISiteDetailsProps & ISiteDetailsExtProps & ISiteDetailsDispatch & RoutedProps
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const isConnected = this.props.selectedSite === this.props.siteId;
    const listSite = this.props.siteList!.find((curSite) => curSite.id === this.props.siteId);
    // up-to-date alarm counts for the connected site
    const siteSelectedForDetails: Site | undefined =
      isSet(listSite) && isConnected && isSet(this.props.connectedUnackAlarms) ?
        {
          ...listSite,
          unackAlarms: this.props.connectedUnackAlarms,
          unclearedAlarms: this.props.connectedUnclearedAlarms,
        }
      : listSite;
    const hasAlarms = (siteSelectedForDetails?.unackAlarms ?? 0) > 0;
    const isFavorite =
      this.props.favoriteSites?.includes(siteSelectedForDetails?.id ?? -1) ?? false;

    const backButton = (
      <Button className="infoScreenButton" variant="secondary" onClick={() => this.props.onBack()}>
        <FormattedMessage id={messages.msg_show_all_sites.id} />
      </Button>
    );

    if (!isSet(siteSelectedForDetails)) {
      return (
        <InfoScreen
          title={<FormattedMessage id={messages.msg_no_site_selected.id} />}
          footer={backButton}
        />
      );
    }

    return (
      <InfoScreen
        scrollable={true}
        title={
          <span title={`Id: ${siteSelectedForDetails.id}`} className="infoScreenTitle">
            <span
              className="favoriteStarToggle"
              role="button"
              tabIndex={0}
              aria-label={this.context.formatMessage(
                isFavorite ? messages.msg_remove_favorite : messages.msg_set_favorite,
              )}
              title={this.context.formatMessage(
                isFavorite ? messages.msg_remove_favorite : messages.msg_set_favorite,
              )}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  this.props.onToggleFavorite(siteSelectedForDetails.id, !isFavorite);
                }
              }}
              onClick={(event) => {
                event.stopPropagation();
                this.props.onToggleFavorite(siteSelectedForDetails.id, !isFavorite);
              }}
            >
              {isFavorite ?
                <IconFavorite className="favoriteStarFilled" />
              : <IconEmptyFavorite />}
            </span>
            {siteSelectedForDetails.name}
            {isConnected && this.props.isOffline && (
              <span className="infoScreenBadge">
                <FormattedMessage id={messages.msg_offline.id} />
              </span>
            )}
          </span>
        }
        footer={
          <div className="buttonbox">
            {backButton}
            {isConnected === true ?
              <Button
                className="infoScreenButton"
                variant="danger"
                onClick={(event) => {
                  this.props.onDisconnectClick();
                  this.props.onBack();
                }}
              >
                <FormattedMessage id={messages.msg_disconnect.id} />
              </Button>
            : <Button
                className="infoScreenButton"
                variant="primary"
                onClick={() => {
                  isSet(siteSelectedForDetails) &&
                    this.props.onSelectSite(siteSelectedForDetails.id);
                  this.props.onBack();
                }}
              >
                <FormattedMessage id={messages.msg_connect.id} />
              </Button>
            }
            {this.props.onShowInMap &&
              !this.props.isPopupOpenInMap &&
              SiteModel.hasValidCoordinates(siteSelectedForDetails) && (
                <Button
                  className="infoScreenButton showInMapButton"
                  variant="secondary"
                  onClick={this.props.onShowInMap}
                >
                  <FormattedMessage id={messages.msg_show_in_map.id} />
                </Button>
              )}
          </div>
        }
      >
        <table>
          <tbody>
            {isSet(siteSelectedForDetails.cloudSettings?.reportsSettings?.address) && (
              <InfoRow
                key="details_adress"
                icon={<IconLocation />}
                label={<FormattedMessage id={messages.msg_address.id} />}
                value={siteSelectedForDetails.cloudSettings?.reportsSettings?.address}
              />
            )}
            <InfoRow
              key="details_snr"
              icon={<IconGrid />}
              label={<FormattedMessage id={messages.msg_serialnumber.id} />}
              value={
                <div
                  title={
                    isSet(siteSelectedForDetails.ipAddresses) ?
                      `${this.context.formatMessage(messages.msg_network)}:\n${siteSelectedForDetails.ipAddresses
                        .split(",")
                        .map((ip) => ip.trim())
                        .join("\n")}`
                    : undefined
                  }
                >
                  {siteSelectedForDetails.serial}
                </div>
              }
            />
            <InfoRow
              key="details_device"
              icon={<IconRocket />}
              label={<FormattedMessage id={messages.msg_device.id} />}
              value={<FormattedMessage id={`DeviceType_${siteSelectedForDetails.deviceType}`} />}
            />
            {isSet(siteSelectedForDetails.deviceSw) && siteSelectedForDetails.deviceSw !== "" && (
              <InfoRow
                key="details_software"
                icon={<IconCode />}
                label={<FormattedMessage id={messages.msg_software.id} />}
                value={siteSelectedForDetails.deviceSw.replace(/-g[0-9a-f]{8}$/, "")}
              />
            )}
            {isConnected && (
              <InfoRow
                key="details_trend_live"
                icon={<IconClock />}
                label={<FormattedMessage id={messages.msg_trend_live_data.id} />}
                value={
                  <>
                    <MyFormattedTime
                      valueMs={this.props.lastTrendData}
                      showSeconds={true}
                      nullText={"-"}
                    />{" "}
                    /{" "}
                    <MyFormattedTime
                      valueMs={this.props.lastLiveData}
                      showSeconds={true}
                      nullText={"-"}
                    />{" "}
                    (
                    {this.props.liveViaProxy ?
                      <FormattedMessage id={messages.msg_direct.id} />
                    : <FormattedMessage id={messages.msg_cloud.id} />}
                    )
                  </>
                }
              />
            )}
            {isConnected && isSet(this.props.parameterTimestamp) && (
              <InfoRow
                key="details_parameters"
                icon={<IconCalendar />}
                label={<FormattedMessage id={messages.msg_parameters.id} />}
                value={
                  <>
                    <MyFormattedDate valueMs={this.props.parameterTimestamp * 1000} />{" "}
                    <MyFormattedTime valueMs={this.props.parameterTimestamp * 1000} />
                  </>
                }
              />
            )}
            {hasAlarms && (
              <InfoRow
                key="details_network"
                icon={<IconAlarm />}
                label={<></>}
                value={
                  <SiteInfoTags
                    site={siteSelectedForDetails}
                    offline={isConnected && this.props.isOffline}
                  />
                }
              />
            )}
          </tbody>
        </table>
      </InfoScreen>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SiteDetailsComponent));
